import Joi from 'joi'
import { useEffect } from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { joiResolver } from '@hookform/resolvers/joi'

import Btn from '../../../../../../UI/Btn'
import Checkbox from '../../../../../../UI/Checkbox'
import SelectField from '../../../../../../UI/SelectField'

import SettingHint from '../SettingHint'

import { TCompanies } from '../../../../../../shared/const'
import { mapCompanyTypeToName } from '../../../../../../shared/helpers'

import styles from '../style.module.scss'

/*
 * Die Einstellung beantwortet eine Frage: Ab welchem Bearbeitungsstand ist eine
 * Junior Company auf der öffentlichen Übersichtsseite zu sehen?
 *
 * Vorher stand hier eine Einfachauswahl, die genau eine Gruppe zeigte — „Zu
 * überprüfen" blendete also die bereits aktivierten Companies aus. Das war
 * selten gemeint. Die Stände bauen aufeinander auf, deshalb ist die Auswahl
 * jetzt eine Schwelle: alles ab dem gewählten Stand erscheint.
 */
export const STAGES = [
	{
		// Die Reihenfolge ist der Lebenslauf einer Company: registriert →
		// eingereicht → aktiviert. `statuses` ist die Schwelle und alles darüber.
		value: 'ACTIVATED',
		label: 'Only after activation',
		statuses: ['ACTIVATED']
	},
	{
		value: 'IS_NEED_REVIEW',
		label: 'From submission for review onwards',
		statuses: ['IS_NEED_REVIEW', 'ACTIVATED']
	},
	{
		value: 'NOT_ACTIVATED',
		label: 'From registration onwards',
		statuses: ['NOT_ACTIVATED', 'IS_NEED_REVIEW', 'ACTIVATED']
	}
]

const COMPANY_TYPES = [TCompanies.JC, TCompanies.JCC, TCompanies.JBC]

const schema = Joi.object({
	stage: Joi.string()
		.valid(...STAGES.map(stage => stage.value))
		.required(),
	// Ohne Mindestanzahl ließe sich „keine Unternehmensform" speichern. Eine
	// leere Liste bedeutet im Backend aber „keine Einschränkung" — die Seite
	// zeigte dann alle Formen statt keiner.
	companyTypes: Joi.array().items(Joi.string()).min(1).required().messages({
		'array.min': 'Please select at least one company type'
	})
})

/*
 * Gespeichert wird `{ statuses, companyTypes }`. Ältere Bestände tragen an
 * derselben Stelle noch einen einzelnen Stand als Text ("ACTIVATED") — das
 * Backend versteht beides, hier wird es beim Lesen umgerechnet.
 */
export const readStage = value => {
	const statuses = typeof value === 'string' ? [value] : Array.isArray(value) ? value : value?.statuses

	if (!Array.isArray(statuses) || !statuses.length) return STAGES[0].value

	// Der früheste enthaltene Stand ist die Schwelle.
	const stage = [...STAGES].reverse().find(item => statuses.includes(item.value))
	return stage?.value || STAGES[0].value
}

export const readCompanyTypes = value => {
	const stored = Array.isArray(value?.companyTypes) ? value.companyTypes : []
	const known = stored.filter(type => COMPANY_TYPES.includes(type))

	// Bestand ohne Auswahl: Bisher erschienen alle Unternehmensformen, also
	// stehen sie auch alle angehakt da.
	return known.length ? known : COMPANY_TYPES
}

export default function CompanyPresentationType(props) {
	const { t } = useTranslation()
	const {
		reset,
		control,
		register,
		handleSubmit,
		formState: { errors }
	} = useForm({
		resolver: joiResolver(schema)
	})

	const onSubmit = form => {
		const stage = STAGES.find(item => item.value === form.stage) || STAGES[0]

		props
			.updateOptions({
				name: 'companyPresentationType',
				value: {
					statuses: stage.statuses,
					companyTypes: form.companyTypes
				}
			})
			.then(() => toast.success(t('Options was updated')))
	}

	const stageOptions = STAGES.map(stage => ({ value: stage.value, label: t(stage.label) }))

	useEffect(() => {
		const value = props.form?.companyPresentationType
		reset({
			stage: readStage(value),
			companyTypes: readCompanyTypes(value)
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.form])

	return (
		<form id="companyPresentationType" className={styles.root} onSubmit={handleSubmit(onSubmit)}>
			<section className={styles.section}>
				<h3 className={styles.sectionTitle}>{t('Company Presentation Status')}</h3>
				<SettingHint
					example={t(
						'Example: "From submission for review onwards" shows companies awaiting a decision as well as activated ones — but not those still being edited.'
					)}
					warning={t(
						'Affects the public overview page only. Inside the application every role keeps seeing the companies it is entitled to see.'
					)}
				>
					{t(
						'From which stage of processing a Junior Company appears on the public overview page. The stages build on one another: everything from the selected stage onwards is shown.'
					)}
				</SettingHint>
				<div className={styles.sectionContent}>
					<div className={styles.flexContainer}>
						<SelectField
							className={styles.field}
							label={t('Publish companies from this stage onwards')}
							placeholder={t('Please select')}
							options={stageOptions}
							control={control}
							name="stage"
							error={errors.stage}
						/>
					</div>

					<h4 className={styles.miniSectionTitle}>{t('Company types')}</h4>
					<p className={styles.hintLine}>
						{t('Only the company types ticked here appear on the public overview page.')}
					</p>
					{errors.companyTypes && (
						<p className={styles.errorLine}>{t('Please select at least one company type')}</p>
					)}
					<div className={styles.choiceRow}>
						{COMPANY_TYPES.map(type => (
							<Checkbox
								key={type}
								label={`${type} — ${mapCompanyTypeToName(type)}`}
								value={type}
								register={register('companyTypes')}
							/>
						))}
					</div>

					<div className={styles.btnContainer}>
						<Btn
							className={styles.btn}
							type="submit"
							icon="arrow"
							iconSize={{ w: 10, h: 10 }}
							iconColor="#fff"
						>
							{t('save')}
						</Btn>
					</div>
				</div>
			</section>
		</form>
	)
}

CompanyPresentationType.propTypes = {
	form: PropTypes.object.isRequired,
	updateOptions: PropTypes.func.isRequired
}
